<template>
  <div class="project-limits-table">
    <CustomTable
      :title="tableTitle"
      :rowsData="rowsData"
      :columns="projectLimitsColumns"
      :loading="isFetching"
      :show-search="false"
      :custom-body="true"
      :add-btn="true"
      :on-add-click="onAddClick"
    >
      <template #top-right>
        <CustomIcon
          icon="refresh"
          title="Обновить"
          :loading="isFetching"
          class="tw-mr-2"
          @click="loadData"
        />
        <CustomButton
          class="tw-mr-2"
          :loading="isFillLoading"
          :disable="isFetching"
          @click="onFillClick"
        >
          Заполнить
        </CustomButton>
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.unit_name ? `${props.row.material_name} (${props.row.unit_name})` : props.row.material_name }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.unit_name }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ numberWithCommas(props.row.limit_amount) }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center" style="width: 100px; min-width: 100px;">
            <div class="tw-flex tw-justify-center tw-items-center tw-gap-2">
              <CustomIcon
                icon="edit"
                title="Редактировать"
                @click="onEditClick(props.row)"
              />
              <CustomIcon
                icon="trash"
                title="Удалить"
                :approve-mode="true"
                :approve="() => onDeleteClick(props.row)"
              />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
    <NotFoundText v-if="!isFetching && rowsData.length === 0" />
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import CustomButton from "@/components/common/CustomButton.vue";
import NotFoundText from "@/components/common/NotFoundText";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { numberWithCommas } from "@/services";
import { MODAL_NAMES } from "@/constants";
import {
  getSbsProjectMaterialLimits,
  addSbsProjectMaterialLimit,
  updateSbsProjectMaterialLimit,
  deleteSbsProjectMaterialLimit,
  fillSbsProjectMaterialLimits,
  projectLimitsColumns,
} from "./services";

const props = defineProps({
  sbsProjectId: {
    type: Number,
    default: null,
  },
  projectTypeId: {
    type: Number,
    default: null,
  },
  projectName: {
    type: String,
    default: "",
  },
  constructiveName: {
    type: String,
    default: "",
  },
});

const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();

const rowsData = ref([]);
const isFetching = ref(false);
const isFillLoading = ref(false);

const tableTitle = computed(() => {
  if (props.projectName && props.constructiveName) {
    return `Лимиты: ${props.projectName} — ${props.constructiveName}`;
  }
  return "Лимиты материалов по проекту";
});

async function loadData() {
  if (!props.projectTypeId || !props.sbsProjectId) {
    rowsData.value = [];
    return;
  }
  isFetching.value = true;
  const res = await getSbsProjectMaterialLimits(
    props.projectTypeId,
    props.sbsProjectId
  );
  isFetching.value = false;
  rowsData.value = res ?? [];
}

watch(
  () => [props.projectTypeId, props.sbsProjectId],
  () => {
    if (props.projectTypeId && props.sbsProjectId) loadData();
    else rowsData.value = [];
  },
  { immediate: true }
);

function onAddClick() {
  appStore.showModal(MODAL_NAMES.AddProjectMaterialLimit, {
    title: "Добавить лимит материала",
    project_type_id: props.projectTypeId,
    sbs_project_id: props.sbsProjectId,
    onSubmit: async (_ptId, _sbsId, body) => {
      const res = await addSbsProjectMaterialLimit(
        props.projectTypeId,
        props.sbsProjectId,
        body
      );
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

function onEditClick(row) {
  appStore.showModal(MODAL_NAMES.EditProjectMaterialLimit, {
    title: "Редактировать лимит",
    project_type_id: props.projectTypeId,
    sbs_project_id: props.sbsProjectId,
    body: {
      sbs_project_material_limit_id: row.sbs_project_material_limit_id,
      material_id: row.material_id,
      limit_amount: row.limit_amount,
      material_name: row.material_name,
      unit_name: row.unit_name,
    },
    onSubmit: async (_ptId, _sbsId, payload) => {
      const res = await updateSbsProjectMaterialLimit(
        props.projectTypeId,
        props.sbsProjectId,
        payload
      );
      if (!res) return;
      showSucceedSnackbar();
      appStore.closeModal();
      await loadData();
    },
  });
}

async function onDeleteClick(row) {
  const res = await deleteSbsProjectMaterialLimit(
    props.projectTypeId,
    props.sbsProjectId,
    row.sbs_project_material_limit_id
  );
  if (!res) return;
  showSucceedSnackbar();
  await loadData();
}

async function onFillClick() {
  if (!props.projectTypeId || !props.sbsProjectId) return;
  isFillLoading.value = true;
  const res = await fillSbsProjectMaterialLimits(
    props.projectTypeId,
    props.sbsProjectId
  );
  isFillLoading.value = false;
  if (!res) return;
  showSucceedSnackbar();
  await loadData();
}
</script>

<style lang="scss" scoped>
.project-limits-table {
  padding: 0;
}
</style>
